import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Check, CreditCard, Loader2, ShieldCheck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import AuthModal from "@/components/AuthModal";
import SEO from "@/components/SEO";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent } from "@/lib/analytics";
import { CREDIT_PACKS, FREE_ANALYSES, type CreditPackType } from "@/lib/pricing";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import logo from "@/assets/logo.png";
import { useShieldCredits } from "@/hooks/useShieldCredits";

export default function Pricing() {
  const { user, session, loading: authLoading } = useAuth();
  const [authOpen, setAuthOpen] = useState(false);
  const [loading, setLoading] = useState<CreditPackType | null>(null);
  const [params, setParams] = useSearchParams();
  const { credits, isLoading: creditsLoading, refetch } = useShieldCredits();
  const [purchase, setPurchase] = useState<
    | { phase: "checking" | "pending" | "failed" }
    | { phase: "success"; added: number; balance: number }
    | null
  >(null);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId || !session) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let attempts = 0;
    const confirm = async () => {
      if (attempts === 0) setPurchase({ phase: "checking" });
      const { data, error } = await supabase.functions.invoke("verify-credit-purchase", { body: { session_id: sessionId } });
      if (cancelled) return;
      if (!error && (data?.fulfilled || data?.duplicate)) {
        refetch();
        setPurchase({ phase: "success", added: Number(data.credits_added ?? 0), balance: Number(data.balance ?? 0) });
        trackEvent("checkout_confirmed", { credits: Number(data.credits_added ?? 0) });
        const next = new URLSearchParams(params);
        next.delete("session_id");
        setParams(next, { replace: true });
      } else if (!error && data?.status === "pending" && ++attempts < 10) {
        setPurchase({ phase: "pending" });
        timer = setTimeout(confirm, 3000);
      } else {
        setPurchase({ phase: "failed" });
      }
    };
    confirm();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [sessionId, session, refetch, setParams]);
  const canceled = params.get("payment") === "canceled";

  const checkout = async (type: CreditPackType) => {
    if (!user || !session) { setAuthOpen(true); return; }
    // Open synchronously so browsers don't block the checkout tab after the request.
    const checkoutTab = window.open("about:blank", "_blank");
    if (checkoutTab) checkoutTab.opener = null;
    setLoading(type);
    trackEvent("checkout_started", { plan: type });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { type },
      });
      if (error || !data?.url) throw new Error("Checkout unavailable");
      const url = new URL(data.url);
      if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com") throw new Error("Invalid checkout destination");
      if (checkoutTab) checkoutTab.location.href = url.href;
      else window.location.assign(url.href);
    } catch {
      checkoutTab?.close();
      toast.error("Couldn't open checkout. Please try again.");
    } finally { setLoading(null); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="GreyRock AI® Pricing — 3 Free, Packs from $4.99"
        description="Try 3 free text message analyses with no signup. Then buy one-time credit packs from $4.99 — no subscription, no recurring charges, credits never expire."
        path="/pricing"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "GreyRock AI®",
            applicationCategory: "LifestyleApplication",
            operatingSystem: "Web",
            url: "https://greyrockai.app/pricing",
            offers: [
              { "@type": "Offer", name: `${FREE_ANALYSES} free analyses`, price: "0", priceCurrency: "USD" },
              ...CREDIT_PACKS.map((p) => ({ "@type": "Offer", name: p.name, price: String(p.price), priceCurrency: "USD", url: "https://greyrockai.app/pricing" })),
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://greyrockai.app/" },
              { "@type": "ListItem", position: 2, name: "Pricing", item: "https://greyrockai.app/pricing" },
            ],
          },
        ]}
      />
      <header className="border-b border-border">
        <nav aria-label="Main navigation" className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-4">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg">
            <img src={logo} width={32} height={32} alt="Toxicity Neutralizer" className="h-8 w-8 object-contain" />
            <span>GreyRock <span className="text-primary">AI<sup className="ml-0.5 text-xs">®</sup></span></span>
          </Link>
          <div className="flex items-center gap-1">
            <Button variant="ghost" asChild><Link to="/resources">Resources</Link></Button>
            {user ? <Button variant="outline" asChild><Link to="/dashboard">My workspace <ArrowRight /></Link></Button> : <Button variant="outline" onClick={() => setAuthOpen(true)}>Sign in</Button>}
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-5">
        <section className="border-b border-border py-12 md:py-16">
          <p className="mb-4 font-mono text-xs uppercase text-primary">GreyRock AI® / Pricing</p>
          <h1 className="max-w-3xl text-4xl font-bold leading-tight md:text-5xl">{FREE_ANALYSES} free analyses.<br /><span className="text-silver">No signup.</span></h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground">Start with the message that's been on your mind. When you want to go further, buy credits once — from $4.99.</p>
          <div className="mt-7 flex flex-wrap items-center gap-5">
            <Button size="lg" asChild><Link to={user ? "/?preview=landing#hero-analyzer" : "/#hero-analyzer"}><Zap /> Try a free analysis <ArrowRight /></Link></Button>
            <span className="flex items-center gap-2 text-sm text-silver"><ShieldCheck className="h-4 w-4 text-primary" /> No card needed</span>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">Free previews identify patterns. Full response strategies are available with credits.</p>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">The 3 no-signup tries use the browser-only free analyzer. Separately, creating an account gives you 3 welcome credits for the paid analysis engine.</p>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">Only the free analyzer keeps your message in your browser. Paid analyses are processed by AI and stored for your saved reports. <Link to="/privacy" className="text-primary underline underline-offset-4">Read our Privacy Policy</Link>.</p>
        </section>

        <section className="py-10 md:py-12" aria-labelledby="packs-title">
          {user && <p className="mb-5 text-sm text-silver" aria-live="polite">Your balance: {creditsLoading ? "Loading…" : `${credits} credits`}</p>}
          {sessionId && !user && <Button className="mb-5" onClick={() => setAuthOpen(true)}>Sign in to confirm your purchase</Button>}
          {(purchase || canceled) && (
            <div role="status" aria-live="polite" className={cn("mb-8 rounded-lg border p-6 md:p-8", purchase?.phase === "success" ? "border-primary bg-primary/10" : "border-border bg-card")}>
              {purchase?.phase === "success" ? (
                <>
                  <div className="flex items-center gap-3 text-primary"><span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20"><Check className="h-5 w-5" /></span><p className="font-mono text-xs uppercase">Payment confirmed</p></div>
                  <h2 className="mt-4 text-3xl font-bold">+{purchase.added} credits added</h2>
                  <p className="mt-2 text-muted-foreground">Your new balance is <span className="font-semibold text-foreground">{purchase.balance} credits</span>. They never expire — it's safe to close this tab.</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button asChild><Link to="/dashboard">Start an analysis <ArrowRight /></Link></Button>
                    <Button variant="ghost" onClick={() => setPurchase(null)}>Dismiss</Button>
                  </div>
                </>
              ) : purchase?.phase === "failed" ? (
                <>
                  <h2 className="text-xl font-semibold">We couldn't confirm your payment yet</h2>
                  <p className="mt-2 text-sm text-muted-foreground">If you were charged, your credits will still arrive automatically. Refresh this page to check again, or contact support.</p>
                  <Button className="mt-4" variant="outline" onClick={() => window.location.reload()}>Check again</Button>
                </>
              ) : purchase ? (
                <div className="flex items-start gap-3">
                  <Loader2 className="mt-1 h-5 w-5 animate-spin text-primary" />
                  <div><h2 className="text-xl font-semibold">{purchase.phase === "checking" ? "Confirming your payment…" : "Waiting for payment confirmation…"}</h2><p className="mt-1 text-sm text-muted-foreground">Keep this tab open — your credits will appear here in a few seconds.</p></div>
                </div>
              ) : (
                <p className="text-sm text-silver">Payment canceled. No credits were added and you weren't charged.</p>
              )}
            </div>
          )}
          <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
            <div><h2 id="packs-title" className="text-2xl font-semibold">One-time credit packs</h2><p className="mt-2 text-muted-foreground">No subscription. No recurring charges. Credits never expire.</p></div>
            <span className="flex items-center gap-2 text-sm text-silver"><CreditCard className="h-4 w-4 text-primary" /> Prices in USD</span>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {CREDIT_PACKS.map(pack => (
              <article key={pack.type} className={cn("flex flex-col rounded-lg border bg-card p-6", pack.type === "pro" ? "border-primary" : "border-border")}>
                <div className="flex min-h-7 flex-wrap items-center justify-between gap-2"><h3 className="font-semibold">{pack.name}</h3>{pack.type === "pro" && <span className="text-xs font-medium text-primary">Best value</span>}</div>
                <p className="mt-5 text-4xl font-bold">${pack.price.toFixed(2)}<span className="ml-2 text-sm font-normal text-muted-foreground">once</span></p>
                <p className="mt-2 text-lg font-semibold text-primary">{pack.credits} credits</p>
                <p className="mt-1 text-xs text-muted-foreground">About ${(pack.price / pack.credits).toFixed(2)} per credit</p>
                <p className="mt-5 min-h-12 text-sm leading-relaxed text-silver">{pack.description}</p>
                <ul className="my-5 space-y-3 border-t border-border pt-5 text-sm text-silver">
                  {[`${pack.credits} credits added after payment`, "For analyses and response strategies", "Use for rehearsal and content tools", "Credits never expire"].map(item => <li key={item} className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{item}</li>)}
                </ul>
                <Button variant={pack.type === "pro" ? "default" : "secondary"} className="mt-auto w-full" disabled={authLoading || loading !== null} onClick={() => checkout(pack.type)}>
                  {loading === pack.type ? <><Loader2 className="animate-spin" /> Opening checkout…</> : <>Buy {pack.name} <ArrowRight /></>}
                </Button>
              </article>
            ))}
          </div>
          <p className="mt-5 text-center text-sm text-muted-foreground">An account is required to purchase and keep your credits. The free analyses don't require signup.</p>
        </section>

        <section className="border-t border-border py-10 md:py-12" aria-labelledby="questions-title">
          <h2 id="questions-title" className="mb-6 text-2xl font-semibold">Before you choose</h2>
          <div className="grid gap-8 md:grid-cols-3">
            <div><h3 className="font-semibold">Is this a subscription?</h3><p className="mt-2 text-sm leading-relaxed text-muted-foreground">No. Each pack is a single purchase. Buy more only when you decide you need them.</p></div>
            <div><h3 className="font-semibold">What does the free analysis include?</h3><p className="mt-2 text-sm leading-relaxed text-muted-foreground">A preview of the communication pattern, matched privately in your browser. Paid credits unlock deeper analysis and response tools.</p></div>
            <div><h3 className="font-semibold">Can I use credits across tools?</h3><p className="mt-2 text-sm leading-relaxed text-muted-foreground">Yes. The credit cost is shown before each action, so you can choose how to spend your balance.</p></div>
          </div>
        </section>
      </main>
      <footer className="border-t border-border px-5 py-7">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 text-xs text-muted-foreground"><p>© 2026 GreyRock AI®. All Rights Reserved.</p><div className="flex flex-wrap gap-5">{[["/about", "About"], ["/contact", "Contact"], ["/privacy", "Privacy Policy"], ["/terms", "Terms"], ["/refunds", "Refund Policy"]].map(([path, label]) => <Link key={path} to={path} className="hover:text-primary">{label}</Link>)}</div></div>
      </footer>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </div>
  );
}